

import React, { createContext, useEffect, useState } from "react";
import { userService } from "../Services";
import { ApiClient } from "../Constants";
import { useHistory, useLocation } from "react-router-dom";
import { useAlert } from "react-alert";

export interface Employee {
  firstName: string;
  middleName: string;
  lastName: string;
  email: string;
  employee_number: string;
  phone_number: string;
  date_of_employment: string;
  date_of_birth: string;
  email_verified_at: string;
  role: string;
}

export interface AuthState {
  user: Employee | null;
  loading: boolean;
  logout: () => void;
  fetchData: () => void;
  token: string | null;
  setUser: (user: Employee) => void;
  ready: boolean;
  role: string | null;
}

const initialValues: AuthState = {
  logout: () => {},
  user: null,
  setUser: () => {},
  loading: false,
  fetchData: () => {},
  token: null,
  ready: false,
  role: null,
};

interface IOptions {
  children?: React.ReactNode;
}

export const AuthContext = React.createContext<AuthState>(initialValues);
export const AuthConsumer = AuthContext.Consumer;

export const AuthProvider = ({ children }: IOptions) => {
  const history = useHistory();
  const location = useLocation();

  const alert = useAlert();
  const [user, setUser] = useState<Employee | null>(null);
  const [loading, setLoading] = useState(true); // Start with loading true
  const [ready, setReady] = useState(false);
  const [role, setRole] = useState<string | null>(null);
  const token = localStorage.getItem("token");

  // Keep track of whether we've already redirected to prevent redirect loops
  const [hasRedirected, setHasRedirected] = useState(false);

  const logout = async () => {
    await ApiClient.unsetToken();
    setUser(null);
    setRole(null);
    alert.success("Successfully Logout");
    history.push("/");
  };

  const fetchData = async (redirectIfValid?: string) => {
    setLoading(true);

    try {
      const response: any = await userService.authenticatedUser();
      if (response.status === 200 && response.data && response.data.data) {
        const userData = response.data.data;
        setUser(userData);
        setRole(userData.role);
       

        // Only redirect if we haven't already redirected and current path is login
        if (!hasRedirected && (location.pathname === "/" || !location.pathname)) {
          const destination = userData.role === "Warehouse Manager" 
            ? "/warehousemanagerdashboard" 
            : "/dashboard";
          
          console.log("Redirecting to:", destination);
          setHasRedirected(true); // Mark that we've redirected
          history.push(destination);
        }
      } else {
        console.error("Unexpected response structure:", response);
        if (!hasRedirected) {
          setHasRedirected(true);
          history.push("/");
        }
      }
    } catch (error: any) {
      console.error("Authentication error:", error);
      setUser(null);
      setRole(null);

      // Handle specific redirects
      if (
        redirectIfValid === "/forget-password" ||
        redirectIfValid?.includes("/reset-password/") ||
        redirectIfValid?.includes("/verify-email/")
      ) {
        if (!hasRedirected) {
          setHasRedirected(true);
          history.push(redirectIfValid);
        }
      } else if (redirectIfValid && user !== null) {
        if (!hasRedirected) {
          setHasRedirected(true);
          history.push(redirectIfValid);
        }
      } else if (!hasRedirected) {
        setHasRedirected(true);
        history.push("/");
      }
    } finally {
      setLoading(false);
      setReady(true);
    }
  };

  useEffect(() => {
    if (token) {
      fetchData(location.pathname);
    } else {
      setLoading(false);
      setReady(true);
      if (
        !hasRedirected &&
        location.pathname !== "/" && 
        !location.pathname.includes("/forget-password") && 
        !location.pathname.includes("/reset-password/") && 
        !location.pathname.includes("/verify-email/")
      ) {
        setHasRedirected(true);
        history.push("/");
      }
    }
  }, []);

  return (
    <AuthContext.Provider
      value={{ user, logout, setUser, loading, fetchData, ready, token, role }}
    >
      {children}
    </AuthContext.Provider>
  );
};